import React, { forwardRef, PropsWithChildren, ReactNode, useRef } from 'react';

import { assignInlineVars } from '@vanilla-extract/dynamic';

import { useResizeObserver } from '../../hooks/useResizeObserver';

import * as css from './Input.css';
import { Text } from '../Text';

export type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
    /** Optional label displayed above the input field. */
    label?: string;

    /** Visual size variant of the input (controls height/padding). */
    size?: css.InputVariants['size'];

    /** Typography variant applied to the input text. */
    font?: css.InputVariants['font'];

    /** Visual style variant (e.g. default, subtle, etc.). */
    variant?: css.InputVariants['variant'];

    /** Theme variant controlling color scheme. */
    theme?: css.InputVariants['theme'];

    /** Element rendered inside the input on the right side (e.g. icon, button). */
    overlay?: ReactNode;

    /** Maximum width of the input container in pixels. */
    maxWidth?: number;

    /** Error message displayed below the input. */
    error?: string;
};

type InputOverlayProps = PropsWithChildren<{
    overlay?: ReactNode;
    size?: css.InputVariants['size'];
}>;

const InputOverlay = forwardRef<HTMLDivElement, InputOverlayProps>(
    ({ children, overlay, size }: InputOverlayProps, ref): ReactNode => {
        if (!overlay) {
            return children;
        }

        return (
            <div className={css.inputWrapper}>
                {children}
                <div className={css.inputOverlay({ size })} ref={ref}>
                    {overlay}
                </div>
            </div>
        );
    },
);

export const Input = forwardRef<HTMLInputElement, InputProps>(
    ({ label, size, overlay, font, maxWidth, variant, error, theme, ...props }, ref) => {
        const overlayRef = useRef<HTMLDivElement>(null);
        const { width } = useResizeObserver({
            ref: overlayRef,
        });

        return (
            <>
                <div
                    className={css.container}
                    style={assignInlineVars({
                        [css.maxWidth]: maxWidth ? `${maxWidth}px` : 'initial',
                    })}
                >
                    {label && <span className={css.label}>{label}</span>}
                    <InputOverlay overlay={overlay} size={size} ref={overlayRef}>
                        <input
                            ref={ref}
                            className={css.input({ size, font, variant, theme })}
                            {...props}
                            style={assignInlineVars({
                                [css.overlayPaddingRight]: `${width || 0}px`,
                            })}
                        />
                    </InputOverlay>
                </div>
                {error && <span className={css.error}>{error}</span>}
            </>
        );
    },
);

export const InsetInput = forwardRef<
    HTMLInputElement,
    Omit<InputProps, 'size' | 'variant' | 'theme' | 'font' | 'overlay'>
>(({ label, maxWidth, error, ...props }, ref) => {
    return (
        <div
            className={css.container}
            style={assignInlineVars({
                [css.maxWidth]: maxWidth ? `${maxWidth}px` : 'initial',
            })}
        >
            <input ref={ref} className={css.input({ size: 'large', modal: true })} {...props} />
            <div className={css.insetInputOverlay}>
                <span className={css.label}>
                    <Text variant="h4">{label}</Text>
                </span>
            </div>
            {error && <span className={css.insetError}>{error}</span>}
        </div>
    );
});
